import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaChevronRight, FaChevronLeft, FaExpand, FaCompress } from 'react-icons/fa';
import * as pdfjsLib from 'pdfjs-dist';

// Use worker from node_modules - this ensures version compatibility
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

// #region agent log
const __dbg = (location, message, data, hypothesisId) => {
  fetch('http://127.0.0.1:7607/ingest/bd07fc55-f817-4dd3-93e5-2c1e0bd9b539', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '6a5c44' },
    body: JSON.stringify({
      sessionId: '6a5c44',
      location,
      message,
      data: data || {},
      timestamp: Date.now(),
      hypothesisId: hypothesisId || 'unknown',
    }),
  }).catch(() => {});
};
// #endregion

function PresentationViewer({ src, title = 'عرض تقديمي' }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const canvasWrapRef = useRef(null);
  const pdfRef = useRef(null);
  const renderTaskRef = useRef(null);

  const [pageNum, setPageNum] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [error, setError] = useState(null);

  /** Reliable width on mobile: flex parents often report 0 until layout + ResizeObserver fires */
  const getCanvasContainerWidth = useCallback(() => {
    const wrap = canvasWrapRef.current;
    const outer = containerRef.current;
    const w = wrap?.getBoundingClientRect?.().width ?? wrap?.clientWidth ?? 0;
    const w2 = outer?.getBoundingClientRect?.().width ?? outer?.clientWidth ?? 0;
    const measured = Math.max(w || 0, w2 || 0);
    if (measured >= 40) return measured;
    if (typeof window !== 'undefined') {
      return Math.min(Math.max(window.innerWidth - 32, 280), 1200);
    }
    return 400;
  }, []);

  const renderPage = useCallback(async (num) => {
    const pdf = pdfRef.current;
    const canvas = canvasRef.current;
    if (!pdf || !canvas) {
      // #region agent log
      __dbg('PresentationViewer.js:renderPage', 'early_exit_no_pdf_or_canvas', { num, hasPdf: !!pdf, hasCanvas: !!canvas }, 'H-A');
      // #endregion
      return;
    }

    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
      renderTaskRef.current = null;
    }

    try {
      const page = await pdf.getPage(num);
      let containerWidth = getCanvasContainerWidth();
      if (containerWidth < 10) {
        await new Promise((r) => setTimeout(r, 50));
        containerWidth = getCanvasContainerWidth();
      }
      if (containerWidth < 10) {
        await new Promise((r) => requestAnimationFrame(r));
        containerWidth = getCanvasContainerWidth();
      }
      if (containerWidth < 10) {
        // #region agent log
        __dbg('PresentationViewer.js:renderPage', 'abort_width_below_10', { num, containerWidth }, 'H-D');
        // #endregion
        return;
      }

      const unscaledViewport = page.getViewport({ scale: 1 });
      const scale = containerWidth / unscaledViewport.width;
      
      // Use higher resolution for better quality (especially in fullscreen)
      const pixelRatio = window.devicePixelRatio || 1;
      const outputScale = Math.min(pixelRatio, 2); // Cap at 2x for performance
      const viewport = page.getViewport({ scale: scale * outputScale });

      canvas.height = viewport.height;
      canvas.width = viewport.width;
      canvas.style.width = `${containerWidth}px`;
      canvas.style.height = `${containerWidth / unscaledViewport.width * unscaledViewport.height}px`;

      const ctx = canvas.getContext('2d', { alpha: false });
      if (!ctx) {
        // #region agent log
        __dbg('PresentationViewer.js:renderPage', 'no_2d_context', { num, cw: containerWidth, vw: viewport.width, vh: viewport.height }, 'H-A');
        // #endregion
        return;
      }

      // #region agent log
      __dbg('PresentationViewer.js:renderPage', 'before_pdf_render', {
        num,
        containerWidth,
        unscaledW: unscaledViewport.width,
        scale,
        vw: viewport.width,
        vh: viewport.height,
        canvasW: canvas.width,
        canvasH: canvas.height,
      }, 'H-D');
      // #endregion

      const task = page.render({ canvasContext: ctx, viewport });
      renderTaskRef.current = task;

      await task.promise;
      renderTaskRef.current = null;
      // #region agent log
      __dbg('PresentationViewer.js:renderPage', 'render_promise_done', { num }, 'H-C');
      // #endregion
    } catch (err) {
      if (err.name !== 'RenderingCancelled') {
        console.error('Error rendering page:', err);
        // #region agent log
        __dbg('PresentationViewer.js:renderPage', 'render_catch', { num, name: err.name, message: String(err.message || err) }, 'H-C');
        // #endregion
      }
    }
  }, [getCanvasContainerWidth]);

  useEffect(() => {
    let cancelled = false;

    async function loadPdf() {
      setIsLoading(true);
      setError(null);
      try {
        // #region agent log
        __dbg('PresentationViewer.js:loadPdf', 'getDocument_start', { srcLen: (src && src.length) || 0, srcHost: (() => { try { return new URL(src, window.location.href).hostname; } catch (e) { return 'bad_url'; } })() }, 'H-B');
        // #endregion
        const loadingTask = pdfjsLib.getDocument({
          url: src,
          withCredentials: true,
        });
        const pdf = await loadingTask.promise;
        if (cancelled) return;
        pdfRef.current = pdf;
        setTotalPages(pdf.numPages);
        setPageNum(1);
        // #region agent log
        __dbg('PresentationViewer.js:loadPdf', 'pdf_loaded', { numPages: pdf.numPages }, 'H-B');
        // #endregion
        await renderPage(1);
        // Second pass after layout (mobile flex often has 0 width on first paint)
        requestAnimationFrame(() => {
          if (!cancelled && pdfRef.current) renderPage(1);
        });
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading PDF:', err);
          // #region agent log
          __dbg('PresentationViewer.js:loadPdf', 'pdf_load_failed', { name: err.name, message: String(err.message || err) }, 'H-B');
          // #endregion
          setError('فشل تحميل العرض التقديمي');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    loadPdf();
    return () => { cancelled = true; };
  }, [src, renderPage]);

  useEffect(() => {
    if (pdfRef.current && pageNum > 0) {
      renderPage(pageNum);
    }
  }, [pageNum, renderPage]);

  useEffect(() => {
    const handleResize = () => {
      if (pdfRef.current) renderPage(pageNum);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [pageNum, renderPage]);

  // Re-render when canvas area gets a real width (mobile / flex layout)
  useEffect(() => {
    const wrap = canvasWrapRef.current;
    if (!wrap || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (pdfRef.current) renderPage(pageNum);
    });
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [pageNum, renderPage]);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      setTimeout(() => {
        if (pdfRef.current) renderPage(pageNum);
      }, 100);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, [pageNum, renderPage]);

  const goNext = () => {
    if (pageNum < totalPages) setPageNum(p => p + 1);
  };

  const goPrev = () => {
    if (pageNum > 1) setPageNum(p => p - 1);
  };

  const toggleFullscreen = () => {
    const container = containerRef.current;
    if (!container) return;
    if (!document.fullscreenElement) {
      container.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') goNext();
      if (e.key === 'ArrowRight' || e.key === 'ArrowUp') goPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  });

  if (error) {
    return (
      <div className="aspect-video bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
        <p className="text-gray-500 text-lg">{error}</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`relative w-full min-w-0 rounded-lg overflow-hidden shadow-md select-none ${
        isFullscreen ? 'bg-gray-900 flex flex-col' : 'bg-gray-100'
      }`}
    >
      {/* Top bar with title + controls */}
      <div className="flex items-center justify-between px-3 sm:px-4 py-2 bg-gray-800 text-white" dir="rtl">
        <div className="flex items-center gap-2">
          <img src="/icon.png" alt="Allemni" className="w-5 h-5 sm:w-6 sm:h-6 rounded" />
          <span className="font-bold text-xs sm:text-sm truncate max-w-[200px] sm:max-w-none">{title}</span>
        </div>
        <div className="flex items-center gap-3" dir="ltr">
          <span className="text-white/70 text-xs sm:text-sm font-mono tabular-nums">
            {pageNum} / {totalPages}
          </span>
          <button onClick={toggleFullscreen} className="text-white/70 hover:text-white transition-colors">
            {isFullscreen ? <FaCompress className="text-sm" /> : <FaExpand className="text-sm" />}
          </button>
        </div>
      </div>

      {/* Canvas area — ref used for width; mobile flex often reports 0 until ResizeObserver */}
      <div
        ref={canvasWrapRef}
        className={`relative w-full min-w-0 min-h-[120px] bg-white ${
          isFullscreen ? 'flex-1 flex items-center justify-center overflow-hidden' : ''
        }`}
      >
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-20">
            <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
          </div>
        )}

        <canvas
          ref={canvasRef}
          className={`block ${isFullscreen ? 'max-h-full max-w-full object-contain' : 'w-full'}`}
        />

        {/* Per-page Allemni banner overlay */}
        <div className="absolute bottom-0 left-0 right-0 pointer-events-none z-10" style={{
          background: 'linear-gradient(to right, rgba(0,52,128,0.90), rgba(0,82,204,0.90), rgba(0,52,128,0.90))',
        }}>
          <div className="flex items-center justify-between px-3 py-1.5 sm:px-4 sm:py-2">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <img src="/icon.png" alt="Allemni" className="w-5 h-5 sm:w-6 sm:h-6 rounded" />
              <span className="text-white font-bold text-[10px] sm:text-xs">Allemni</span>
              <span className="text-white/60 text-[8px] sm:text-[10px]">علمني بالدارجة</span>
            </div>
            <span className="text-white/50 text-[8px] sm:text-[10px]">allemni.site</span>
          </div>
        </div>

        {/* Navigation arrows */}
        {totalPages > 1 && (
          <>
            <button
              onClick={goPrev}
              disabled={pageNum <= 1}
              className={`absolute top-1/2 right-2 sm:right-3 -translate-y-1/2 z-10 w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all ${
                pageNum <= 1
                  ? 'bg-black/10 text-white/30 cursor-not-allowed'
                  : 'bg-black/40 text-white hover:bg-black/60 shadow-lg'
              }`}
            >
              <FaChevronRight className="text-sm sm:text-base" />
            </button>
            <button
              onClick={goNext}
              disabled={pageNum >= totalPages}
              className={`absolute top-1/2 left-2 sm:left-3 -translate-y-1/2 z-10 w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all ${
                pageNum >= totalPages
                  ? 'bg-black/10 text-white/30 cursor-not-allowed'
                  : 'bg-black/40 text-white hover:bg-black/60 shadow-lg'
              }`}
            >
              <FaChevronLeft className="text-sm sm:text-base" />
            </button>
          </>
        )}
      </div>

      {/* Bottom navigation bar */}
      <div className="flex items-center justify-center gap-4 px-4 py-2.5 bg-gray-800" dir="ltr">
        <button
          onClick={goPrev}
          disabled={pageNum <= 1}
          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
            pageNum <= 1
              ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
              : 'bg-primary text-white hover:bg-primary-dark'
          }`}
        >
          <span className="flex items-center gap-1"><FaChevronLeft className="text-[10px]" /> السابق</span>
        </button>

        <div className="flex items-center gap-1.5">
          {totalPages <= 15 ? (
            Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setPageNum(i + 1)}
                className={`w-2 h-2 rounded-full transition-all ${
                  pageNum === i + 1
                    ? 'bg-primary w-5'
                    : 'bg-gray-600 hover:bg-gray-500'
                }`}
              />
            ))
          ) : (
            <span className="text-white/70 text-sm font-mono">{pageNum} / {totalPages}</span>
          )}
        </div>

        <button
          onClick={goNext}
          disabled={pageNum >= totalPages}
          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
            pageNum >= totalPages
              ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
              : 'bg-primary text-white hover:bg-primary-dark'
          }`}
        >
          <span className="flex items-center gap-1">التالي <FaChevronRight className="text-[10px]" /></span>
        </button>
      </div>
    </div>
  );
}

export default PresentationViewer;
